import React from "react";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Typography } from "@mui/material";
import BaseTable from "@components/DataTable/BaseTable";
import DryBulkButton from "./ui/DryBulkButton";
import DryBulkSection from "./ui/DryBulkSection";

export interface DryBulkListRow {
  id: number;
  index: number;
  [field: string]: unknown;
}

interface Props {
  testId: string;
  title: string;
  addLabel: string;
  headers: { field: string; columnName: string }[];
  rows: DryBulkListRow[];
  canAdd?: boolean;
  readOnly?: boolean;
  error?: string;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  children?: React.ReactNode;
}

const EMPTY_CELL = "-";
const EMPTY_ACTION_FIELD = "emptyAction";

/** The first column is plain text (the shared table paints it in the link colour). */
const plainFirstColumn = (field?: string) =>
  field
    ? {
        [`& .MuiDataGrid-cell[data-field='${field}'] *`]: {
          color: "text.primary !important",
        },
      }
    : undefined;

/** A form section holding a list edited through a side drawer (IA § 4: cargo lines, activities, resources, attachments). */
const DryBulkListSection: React.FC<Props> = ({
  testId,
  title,
  addLabel,
  headers,
  rows,
  canAdd = true,
  readOnly = false,
  error,
  onAdd,
  onEdit,
  onDelete,
  children,
}) => {
  const isEmpty = rows.length === 0;
  const visibleHeaders = readOnly ? headers.filter(({ field }) => field !== "action") : headers;
  const emptyHeaders = visibleHeaders.map((header) =>
    header.field === "action" ? { ...header, field: EMPTY_ACTION_FIELD } : header,
  );
  const emptyRow: DryBulkListRow = {
    id: 0,
    index: -1,
    ...Object.fromEntries(emptyHeaders.map(({ field }) => [field, EMPTY_CELL])),
  };

  return (
    <DryBulkSection testId={testId} title={title}>
      <div className="flex w-full flex-col gap-4" data-testid={`${testId}-content`}>
        <div data-testid={isEmpty ? `${testId}-empty` : `${testId}-table`}>
          <BaseTable
            rows={isEmpty ? [emptyRow] : rows}
            headers={isEmpty ? emptyHeaders : visibleHeaders}
            customSx={plainFirstColumn(visibleHeaders[0]?.field)}
            onRowEdit={readOnly || isEmpty ? undefined : (row: DryBulkListRow) => onEdit(row.index)}
            onRowDelete={
              readOnly || isEmpty ? undefined : (row: DryBulkListRow) => onDelete(row.index)
            }
          />
        </div>
        {error && (
          <Typography variant="body2" color="error" data-testid={`${testId}-error`}>
            {error}
          </Typography>
        )}
        {!readOnly && (
          <div className="flex justify-end">
            <DryBulkButton
              type="button"
              variant="outlined"
              color="primary"
              testId={`${testId}-add`}
              disabled={!canAdd}
              label={
                <span className="flex items-center gap-2 text-sm font-medium">
                  <AddCircleOutline fontSize="small" />
                  {addLabel}
                </span>
              }
              onClick={onAdd}
              sx={{ height: "40px", minHeight: "40px" }}
            />
          </div>
        )}
        {!readOnly && children}
      </div>
    </DryBulkSection>
  );
};

export default DryBulkListSection;
